import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  outlineHoverVisuals, outlineVisuals,
} from '~/_shared/constants/mapObjects/mapObjectOutline/outlineVisuals.constants';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { createUuid } from '~/_shared/utils/createUuid';
import { type MapObjectOutlineInstance } from '~/map/map/mapObjects/mapOutline/mapOutlineModel';
import { useActiveDrawingInstanceZIndex } from '~/map/zIndexes/useDrawingInstanceZIndex.hook';
import {
  drawingToolDisableDrawingMode, drawingToolItemHovered, drawingToolItemHoverStopped,
} from '~/store/frontendState/mapTools/drawingTool/drawingTool.actionCreators';
import {
  DrawingToolMode, useDrawingToolModeSelector, useMapToolsDrawingActiveToolSelector,
} from '~/store/frontendState/mapTools/drawingTool/drawingTool.selectors';
import { DrawingItemPlacement } from '~/store/mapSettings/drawing/items/drawingItems.types';
import { type LatLng } from '../../../../_shared/types/latLng';
import { DrawingTool } from '../../../../drawingTool/drawingTool.enums';
import { drawingItemsAddItem } from '../../../../store/mapSettings/drawing/items/drawingItems.actionCreators';
import { type DrawingPolygonSettingsState } from '../../../../store/mapSettings/drawing/settings/polygon/drawingPolygonSettings.state';
import { MapOutline } from '../../mapObjects/mapOutline/mapOutline.component';
import { DrawMapShape } from '../../mapObjects/mapShape/drawMapShape.container';
import { MapShapeLine } from '../../mapObjects/mapShape/mapShapeLine.component';
import { type MapShapeInstance } from '../../mapObjects/mapShape/mapShapeModel';

type DrawingToolPolygonCreateNewInstanceProps = Readonly<{
  settings: DrawingPolygonSettingsState;
}>;

export const DrawingToolPolygonCreateNewInstanceContainer: FC<DrawingToolPolygonCreateNewInstanceProps> = ({ settings }) => {
  const [shapeKey, setShapeKey] = useState(createUuid());
  const [shape, setShape] = useState<MapShapeInstance | null>(null);
  const [firstOutlineHover, setFirstOutlineHover] = useState<boolean>(false);
  const drawingToolMode = useDrawingToolModeSelector();
  const activeDrawingTool = useMapToolsDrawingActiveToolSelector();
  const dispatch = useDispatch();
  const zIndex = useActiveDrawingInstanceZIndex();

  const isActive = drawingToolMode === DrawingToolMode.DrawingItems && activeDrawingTool === DrawingTool.Polygon;

  const resetShape = useCallback(() => {
    setShape(null);
    setShapeKey(createUuid());
  }, []);

  useEffect(() => {
    if (!isActive) {
      resetShape();
    }
  }, [isActive, resetShape]);

  const onShapeChange = useCallback((shape: MapShapeInstance) => {
    setShape(shape);
  }, []);

  const onOutlineMouseOver = useCallback((outlineId: Uuid) => {
    if (isFirstOutlineHoverEnabled(shape, outlineId)) {
      setFirstOutlineHover(true);
      dispatch(drawingToolItemHovered());
    }
  }, [dispatch, shape]);

  const onOutlineMouseOut = useCallback(() => {
    setFirstOutlineHover(false);
    dispatch(drawingToolItemHoverStopped());
  }, [dispatch]);

  const renderOutline = useCallback((id: string, outline: MapObjectOutlineInstance) => (
    <MapOutline
      key={id}
      outline={outline}
      visuals={isFirstOutline(shape, outline.id) && firstOutlineHover ? outlineHoverVisuals : outlineVisuals}
      onMouseOver={onOutlineMouseOver}
      onMouseOut={onOutlineMouseOut}
    />
  ), [firstOutlineHover, onOutlineMouseOut, onOutlineMouseOver, shape]);

  const renderLine = useCallback((id: string, start: LatLng, end: LatLng) => {
    const borderColor = convertColorToWebGLColor(settings.strokeColor, settings.strokeOpacity / 100);

    return (
      <MapShapeLine
        key={id}
        id={id}
        start={start}
        end={end}
        visuals={{
          width: settings.strokeWeight,
          style: 'solid',
          color: borderColor,
        }}
      />
    );
  }, [settings.strokeOpacity, settings.strokeColor, settings.strokeWeight]);

  const onDrawingDone = useCallback(() => {
    if (!shape || shape?.outlines?.length < 3) {
      dispatch(drawingToolDisableDrawingMode());
      return;
    }

    dispatch(drawingItemsAddItem(shape.id, {
      type: DrawingTool.Polygon,
      value: {
        id: shape.id,
        outlines: shape.outlines,
        placement: DrawingItemPlacement.Default,
        settings,
      },
    }));

    resetShape();
  }, [dispatch, resetShape, settings, shape]);

  if (!isActive) {
    return null;
  }

  return (
    <DrawMapShape
      key={shapeKey}
      zIndex={zIndex}
      onDone={onDrawingDone}
      onChange={onShapeChange}
      renderOutline={renderOutline}
      renderLine={renderLine}
    />
  );
};

const isFirstOutline = (shape: MapShapeInstance | null, outlineId: string) =>
  shape?.outlines[0]?.id === outlineId;

const isFirstOutlineHoverEnabled = (shape: MapShapeInstance | null, outlineId: string) => {
  const isFirst = isFirstOutline(shape, outlineId);
  return isFirst && !!(shape && shape.outlines.length > 2);
};
